import {
  PAYROLL_ROSTER_FIELD_TYPE,
  ROSTER_FILTER_KIND,
  type PayrollRosterFilterField,
  type PayrollRosterFieldFilters,
  type PayrollRosterDraftMember,
  type PayrollRosterField,
  type PayrollRosterRowFocus,
  type PayrollRosterValue,
  type RosterFieldFilter,
  type RosterFieldValueSummary,
  type RosterFilterKind,
} from "@/payroll/roster/types";
import { formatRosterValue } from "./changes";
import { isBlankRosterValue } from "./members";

// Shown wherever "has no value for this field" is offered or summarized.
export const ROSTER_NO_VALUE_LABEL = "No value";

export const rosterFilterKind = (
  field: PayrollRosterField,
): RosterFilterKind => {
  switch (field.field_type) {
    case PAYROLL_ROSTER_FIELD_TYPE.boolean:
      return ROSTER_FILTER_KIND.boolean;
    case PAYROLL_ROSTER_FIELD_TYPE.number:
      return ROSTER_FILTER_KIND.range;
    case PAYROLL_ROSTER_FIELD_TYPE.text:
      return ROSTER_FILTER_KIND.text;
    default:
      return ROSTER_FILTER_KIND.options;
  }
};

export const emptyRosterFilter = (
  field: PayrollRosterField,
): RosterFieldFilter => {
  switch (rosterFilterKind(field)) {
    case ROSTER_FILTER_KIND.boolean:
      return {
        kind: ROSTER_FILTER_KIND.boolean,
        values: [],
        includeBlank: false,
      };
    case ROSTER_FILTER_KIND.range:
      return {
        kind: ROSTER_FILTER_KIND.range,
        min: "",
        max: "",
        includeBlank: false,
      };
    case ROSTER_FILTER_KIND.text:
      return { kind: ROSTER_FILTER_KIND.text, query: "", includeBlank: false };
    default:
      return {
        kind: ROSTER_FILTER_KIND.options,
        values: [],
        includeBlank: false,
      };
  }
};

// A bound only counts once it parses to a real number, so partial input
// ("-", ".", "1e") narrows nothing instead of matching nothing.
export const parseRosterRangeBound = (bound: string): number | null => {
  const trimmed = bound.trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
};

// Does this filter narrow the roster at all? Used for the active-chip styling,
// the active count, and for skipping entries during matching.
export const isRosterFilterActive = (filter: RosterFieldFilter): boolean => {
  if (filter.includeBlank) return true;

  switch (filter.kind) {
    case ROSTER_FILTER_KIND.options:
    case ROSTER_FILTER_KIND.boolean:
      return filter.values.length > 0;
    case ROSTER_FILTER_KIND.range:
      return (
        parseRosterRangeBound(filter.min) !== null ||
        parseRosterRangeBound(filter.max) !== null
      );
    case ROSTER_FILTER_KIND.text:
      return filter.query.trim().length > 0;
  }
};

// Whether the filter still holds raw text worth keeping in state even though it
// doesn't narrow anything yet — the difference between "not filtering" and "the
// owner is mid-keystroke", which is what keeps the inputs controlled.
export const rosterFilterHasInput = (filter: RosterFieldFilter): boolean => {
  if (isRosterFilterActive(filter)) return true;

  switch (filter.kind) {
    case ROSTER_FILTER_KIND.range:
      return filter.min.length > 0 || filter.max.length > 0;
    case ROSTER_FILTER_KIND.text:
      return filter.query.length > 0;
    default:
      return false;
  }
};

export const activeRosterFilterCount = (
  filters: PayrollRosterFieldFilters,
): number =>
  Object.values(filters).filter((filter) => isRosterFilterActive(filter))
    .length;

export const hasActiveRosterFilter = (
  filters: PayrollRosterFieldFilters,
  fieldKey: string,
): boolean => {
  const filter = filters[fieldKey];
  return filter != null && isRosterFilterActive(filter);
};

// Canonical string key for a stored value, so a number field that holds "30.0"
// in one row and 30 in another is counted and matched as one value.
const rosterValueKey = (
  value: PayrollRosterValue | undefined,
  field: PayrollRosterField,
): string => {
  if (field.field_type === PAYROLL_ROSTER_FIELD_TYPE.number) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return String(numeric);
  }
  if (field.field_type === PAYROLL_ROSTER_FIELD_TYPE.boolean) {
    return value === true || value === "true" ? "true" : "false";
  }
  return String(value);
};

const matchesRosterFilter = (
  value: PayrollRosterValue | undefined,
  field: PayrollRosterField,
  filter: RosterFieldFilter,
): boolean => {
  // A blank cell can only ever match via the explicit "No value" opt-in — a
  // range or substring condition says nothing about a person with no value.
  if (isBlankRosterValue(value)) return filter.includeBlank;

  switch (filter.kind) {
    case ROSTER_FILTER_KIND.options:
      return filter.values.includes(rosterValueKey(value, field));
    case ROSTER_FILTER_KIND.boolean:
      return filter.values.includes(value === true || value === "true");
    case ROSTER_FILTER_KIND.range: {
      const min = parseRosterRangeBound(filter.min);
      const max = parseRosterRangeBound(filter.max);
      // Blank-only filter: nothing with a value belongs in the result.
      if (min === null && max === null) return false;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return false;
      return (
        (min === null || numeric >= min) && (max === null || numeric <= max)
      );
    }
    case ROSTER_FILTER_KIND.text: {
      const query = filter.query.trim().toLocaleLowerCase();
      if (!query) return false;
      return String(value).toLocaleLowerCase().includes(query);
    }
  }
};

// Name / ServiceTitan ID search AND every active field filter (values within a
// single field are OR'd, see `matchesRosterFilter`). Source order is preserved
// so an unsorted table keeps the roster's own ordering.
export const filterPayrollRosterDrafts = <
  T extends Pick<PayrollRosterDraftMember, "member">,
>(
  drafts: T[],
  fields: PayrollRosterField[],
  search: string,
  filters: PayrollRosterFieldFilters,
): T[] => {
  const query = search.trim().toLocaleLowerCase();
  const fieldsByKey = new Map(fields.map((field) => [field.key, field]));
  const active = Object.entries(filters).flatMap(([fieldKey, filter]) => {
    const field = fieldsByKey.get(fieldKey);
    return field && isRosterFilterActive(filter)
      ? [[field, filter] as const]
      : [];
  });

  if (!query && active.length === 0) return drafts;

  return drafts.filter(({ member }) => {
    if (
      query &&
      !`${member.display_name} ${member.service_titan_employee_id}`
        .toLocaleLowerCase()
        .includes(query)
    ) {
      return false;
    }

    return active.every(([field, filter]) =>
      matchesRosterFilter(member.field_values[field.key], field, filter),
    );
  });
};

export const filterPayrollRosterDraftsByFocus = (
  drafts: PayrollRosterDraftMember[],
  focus: PayrollRosterRowFocus,
  departedIds: Set<string>,
): PayrollRosterDraftMember[] => {
  if (focus === "missing") {
    return drafts.filter((draft) => draft.suggestion);
  }
  if (focus === "departed") {
    return drafts.filter(
      (draft) =>
        !draft.suggestion &&
        departedIds.has(draft.member.service_titan_employee_id),
    );
  }
  return drafts;
};

// What the roster ACTUALLY holds for a field: how many people sit on each
// value, how many have none, and (for numbers) the real range. This is what
// makes the controls data-aware — every choice carries its count, number
// ranges are hinted with the roster's own bounds, and a stale value still
// present on someone shows up even if the contract dropped it.
export const summarizeRosterFieldValues = (
  drafts: Pick<PayrollRosterDraftMember, "member">[],
  fields: PayrollRosterField[],
): Record<string, RosterFieldValueSummary> =>
  Object.fromEntries(
    fields.map((field) => {
      const summary: RosterFieldValueSummary = {
        counts: {},
        blank: 0,
        min: null,
        max: null,
      };

      drafts.forEach(({ member }) => {
        const value = member.field_values[field.key];
        if (isBlankRosterValue(value)) {
          summary.blank += 1;
          return;
        }

        const key = rosterValueKey(value, field);
        summary.counts[key] = (summary.counts[key] ?? 0) + 1;

        const numeric = Number(value);
        if (
          field.field_type === PAYROLL_ROSTER_FIELD_TYPE.number &&
          Number.isFinite(numeric)
        ) {
          summary.min =
            summary.min === null ? numeric : Math.min(summary.min, numeric);
          summary.max =
            summary.max === null ? numeric : Math.max(summary.max, numeric);
        }
      });

      return [field.key, summary];
    }),
  );

// The choices to offer for a fixed_option field: the contract's options first
// (in contract order), then any value someone is still on that the contract no
// longer allows — those rows need attention, so they must be findable.
export const rosterFilterOptions = (
  contractOptions: string[],
  summary: RosterFieldValueSummary | undefined,
): string[] => {
  const extras = Object.keys(summary?.counts ?? {})
    .filter((value) => !contractOptions.includes(value))
    .sort((left, right) => left.localeCompare(right));
  return [...contractOptions, ...extras];
};

export const buildPayrollRosterFilterFields = (
  drafts: Pick<PayrollRosterDraftMember, "member">[],
  fields: PayrollRosterField[],
  fieldOptions: Record<string, string[]>,
): PayrollRosterFilterField[] => {
  const summaries = summarizeRosterFieldValues(drafts, fields);

  return fields.map((field) => ({
    field,
    options: rosterFilterOptions(
      fieldOptions[field.key] ?? field.options ?? [],
      summaries[field.key],
    ),
    summary: summaries[field.key],
  }));
};

// Above this many picked values the chip collapses to "any of N"; the full list
// still reaches the owner as the chip's hover title.
const MAX_NAMED_FILTER_VALUES = 2;

// Human-readable tail of an active filter chip, rendered after the field label
// ("Pay plan is Install or no value"). Leads with the operator so the chip
// reads as a sentence rather than a key/value dump. `full` never collapses the
// value list, for the hover title.
const describeRosterFilter = (
  field: PayrollRosterField,
  filter: RosterFieldFilter,
  full: boolean,
): string => {
  const conditions: string[] = [];
  const formatBound = (bound: number) =>
    formatRosterValue(String(bound), field);

  switch (filter.kind) {
    case ROSTER_FILTER_KIND.options:
      if (
        filter.values.length > 0 &&
        (full || filter.values.length <= MAX_NAMED_FILTER_VALUES)
      ) {
        conditions.push(filter.values.join(" or "));
      } else if (filter.values.length > MAX_NAMED_FILTER_VALUES) {
        conditions.push(`any of ${filter.values.length}`);
      }
      break;
    case ROSTER_FILTER_KIND.boolean:
      if (filter.values.length === 1) {
        conditions.push(filter.values[0] ? "Yes" : "No");
      } else if (filter.values.length > 1) {
        conditions.push("Yes or No");
      }
      break;
    case ROSTER_FILTER_KIND.range: {
      const min = parseRosterRangeBound(filter.min);
      const max = parseRosterRangeBound(filter.max);
      if (min !== null && max !== null) {
        conditions.push(
          min === max
            ? formatBound(min)
            : `${formatBound(min)}–${formatBound(max)}`,
        );
      } else if (min !== null) {
        conditions.push(`${formatBound(min)} or more`);
      } else if (max !== null) {
        conditions.push(`${formatBound(max)} or less`);
      }
      break;
    }
    case ROSTER_FILTER_KIND.text:
      if (filter.query.trim()) {
        return `contains “${filter.query.trim()}”${
          filter.includeBlank
            ? ` or is ${ROSTER_NO_VALUE_LABEL.toLocaleLowerCase()}`
            : ""
        }`;
      }
      break;
  }

  if (filter.includeBlank) {
    conditions.push(ROSTER_NO_VALUE_LABEL.toLocaleLowerCase());
  }

  return `is ${conditions.join(" or ")}`;
};

export const rosterFilterSummary = (
  field: PayrollRosterField,
  filter: RosterFieldFilter,
): string => describeRosterFilter(field, filter, false);

// The same sentence with nothing collapsed, for the chip's hover title — so a
// chip reading "is any of 3" can still say exactly which three.
export const rosterFilterDetail = (
  field: PayrollRosterField,
  filter: RosterFieldFilter,
): string => `${field.label} ${describeRosterFilter(field, filter, true)}`;

export const comparePayrollRosterValues = (
  left: PayrollRosterValue | undefined,
  right: PayrollRosterValue | undefined,
  fieldType: PayrollRosterField["field_type"] | "intrinsic_text",
): number => {
  const leftBlank = isBlankRosterValue(left);
  const rightBlank = isBlankRosterValue(right);

  if (leftBlank || rightBlank) {
    if (leftBlank && rightBlank) return 0;
    return leftBlank ? 1 : -1;
  }

  if (fieldType === PAYROLL_ROSTER_FIELD_TYPE.number) {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
      return leftNumber - rightNumber;
    }
  }

  if (fieldType === PAYROLL_ROSTER_FIELD_TYPE.boolean) {
    return (
      Number(left === true || left === "true") -
      Number(right === true || right === "true")
    );
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const normalizePayrollRosterSortValue = (
  value: PayrollRosterValue | undefined,
): PayrollRosterValue | undefined =>
  isBlankRosterValue(value) ? undefined : value;
